import {
  API_PREFIX,
  REALTIME_SILENCE_TIMEOUT_MS,
  SseParser,
  routes,
} from '@sunucupark/shared/lite';
import type { RealtimeEvent } from '@sunucupark/shared';
import type { Clock, Scheduler } from '../../application/ports/clock';
import type { Lock } from '../../application/ports/lock';
import type { Logger } from '../../application/ports/logger';
import type {
  EventBroadcast,
  EventStreamPort,
  RealtimeStatus,
} from '../../application/ports/realtime';
import type { SessionPort } from '../../application/ports/session';
import { AppError } from '../../domain/errors';

/** Lock that makes one tab the leader; the others wait for it to let go. */
const LEADER_LOCK = 'realtime-stream';
const FIRST_RETRY_MS = 1_000;
const MAX_RETRY_MS = 30_000;

export interface RealtimeClientOptions {
  baseUrl: string;
  stream: EventStreamPort;
  session: SessionPort;
  clock: Clock;
  scheduler: Scheduler;
  /** Cross-tab lock: exactly one tab per browser keeps the stream open. */
  lock: Lock;
  /** Optional fan-out to the other tabs of the same browser. */
  broadcast?: EventBroadcast<RealtimeEvent>;
  logger?: Logger;
  apiPrefix?: string;
  /** How long silence (no heartbeat) may last before the stream counts as dead. */
  silenceTimeoutMs?: number;
  /** Back-off jitter; injected so tests are deterministic. */
  random?: () => number;
}

/**
 * The live channel (ADR 0008): one stream per browser, reconnecting with
 * back-off, handing every event to the app. The stream carries hints only —
 * what changed is fetched through the normal API — so a missed event costs a
 * delta sync, never data.
 */
export class RealtimeClient {
  private readonly listeners = new Set<(event: RealtimeEvent) => void>();
  private readonly statusListeners = new Set<(status: RealtimeStatus) => void>();
  private status: RealtimeStatus = 'idle';
  private controller: AbortController | null = null;
  private running = false;
  private unsubscribeBroadcast: (() => void) | null = null;
  private loop: Promise<void> | null = null;

  constructor(private readonly options: RealtimeClientOptions) {}

  /** Events this client saw, whether it holds the stream or another tab does. */
  subscribe(listener: (event: RealtimeEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onStatus(listener: (status: RealtimeStatus) => void): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  currentStatus(): RealtimeStatus {
    return this.status;
  }

  /** Starts listening. The connection loop runs in the background. */
  start(): void {
    if (this.running) return;
    this.running = true;
    this.unsubscribeBroadcast =
      this.options.broadcast?.subscribe((event) => this.deliver(event)) ?? null;
    this.loop = this.options.lock
      .run(LEADER_LOCK, () => this.leaderLoop())
      .catch((error: unknown) => {
        this.options.logger?.warn('The event stream gave up', { error: String(error) });
        this.setStatus('stopped');
      });
  }

  /** Stops listening and lets another tab take the stream. */
  async stop(): Promise<void> {
    this.running = false;
    this.controller?.abort();
    this.controller = null;
    this.unsubscribeBroadcast?.();
    this.unsubscribeBroadcast = null;
    await this.loop?.catch(() => undefined);
    this.loop = null;
    this.setStatus('stopped');
  }

  /** Holds the leader lock and keeps one stream open while the client runs. */
  private async leaderLoop(): Promise<void> {
    let attempt = 0;
    while (this.running) {
      this.setStatus(attempt === 0 ? 'connecting' : 'reconnecting');
      let reconnectAfterMs: number | null = null;
      try {
        reconnectAfterMs = await this.readStream();
        attempt = 0;
      } catch (error) {
        const appError = AppError.from(error);
        if (appError.terminatesSession) {
          this.options.logger?.info('The session ended, closing the event stream');
          this.running = false;
          break;
        }
        if (appError.isCancelled && !this.running) break;
        attempt += 1;
        this.options.logger?.debug('The event stream dropped', { code: appError.code, attempt });
      }
      if (!this.running) break;
      await this.options.scheduler.delay(reconnectAfterMs ?? this.backoffMs(attempt));
    }
    this.setStatus('stopped');
  }

  /**
   * Reads one stream to its end. Returns the delay the server asked for when
   * it ended with `reconnect`, or null when the connection simply stopped.
   */
  private async readStream(): Promise<number | null> {
    const controller = new AbortController();
    this.controller = controller;
    // The event contracts load with the connection, not with the page (ADR 0006).
    const { parseRealtimeEvent } = await import('@sunucupark/shared');
    const headers = await this.options.session.authHeaders();
    let reconnectAfterMs: number | null = null;
    let lastSeen = this.options.clock.now();
    const silenceTimeout = this.options.silenceTimeoutMs ?? REALTIME_SILENCE_TIMEOUT_MS;

    const parser = new SseParser({
      onComment: () => {
        lastSeen = this.options.clock.now();
      },
      onMessage: (message) => {
        lastSeen = this.options.clock.now();
        const event = parseRealtimeEvent(message);
        if (!event) return;
        if (event.type === 'reconnect') reconnectAfterMs = event.data.afterMs;
        this.options.broadcast?.publish(event);
        this.deliver(event);
        if (event.type === 'session.revoked') {
          this.running = false;
          controller.abort();
        }
      },
    });

    const url = `${this.options.baseUrl.replace(/\/$/, '')}${this.options.apiPrefix ?? API_PREFIX}${routes.events}`;
    try {
      for await (const chunk of this.options.stream.open({
        url,
        headers,
        signal: controller.signal,
      })) {
        if (this.status !== 'open') this.setStatus('open');
        parser.feed(chunk);
        if (this.options.clock.now() - lastSeen > silenceTimeout) {
          throw new AppError('TIMEOUT', 'The event stream went silent');
        }
      }
    } finally {
      if (this.controller === controller) this.controller = null;
      controller.abort();
    }
    return reconnectAfterMs;
  }

  private backoffMs(attempt: number): number {
    const exponential = Math.min(FIRST_RETRY_MS * 2 ** Math.max(0, attempt - 1), MAX_RETRY_MS);
    // Spread reconnects so a restarted gateway is not hit by every client at once.
    const jitter = (this.options.random ?? Math.random)();
    return Math.round(exponential * (0.5 + jitter / 2));
  }

  private deliver(event: RealtimeEvent): void {
    for (const listener of this.listeners) listener(event);
  }

  private setStatus(status: RealtimeStatus): void {
    if (this.status === status) return;
    this.status = status;
    for (const listener of this.statusListeners) listener(status);
  }
}
